import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from './input';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

export const AddressAutocomplete = ({ 
  onAddressSelect, 
  initialValue = '',
  placeholder = "Commencez à taper votre adresse..."
}) => {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const debouncedQuery = useDebounce(query, 300);

  // Fetch suggestions from API adresse.data.gouv.fr
  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(searchQuery)}&limit=5`
      );
      const data = await response.json();
      
      if (data.features) {
        setSuggestions(data.features.map(feature => ({
          id: feature.properties.id,
          label: feature.properties.label,
          name: feature.properties.name,
          housenumber: feature.properties.housenumber || '',
          street: feature.properties.street || feature.properties.name,
          postcode: feature.properties.postcode,
          city: feature.properties.city,
          context: feature.properties.context,
          coordinates: feature.geometry.coordinates
        })));
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions(debouncedQuery);
  }, [debouncedQuery, fetchSuggestions]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShowSuggestions(true);
    setHighlightedIndex(-1);
  };

  const handleSelectAddress = (address) => {
    // Build the full street address
    const streetAddress = address.housenumber 
      ? `${address.housenumber} ${address.street}`
      : address.street;
    
    setQuery(address.label);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Pass the selected address back to parent
    onAddressSelect({
      fullAddress: address.label,
      address: streetAddress,
      postalCode: address.postcode,
      city: address.city
    });
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelectAddress(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 3 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="pl-10 pr-10"
          autoComplete="off"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {suggestions.map((address, index) => (
            <div
              key={address.id}
              className={`px-4 py-3 cursor-pointer flex items-start gap-3 transition-colors ${
                index === highlightedIndex 
                  ? 'bg-[hsl(207,78%,95%)]' 
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => handleSelectAddress(address)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <MapPin className="w-4 h-4 text-[hsl(207,78%,42%)] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[hsl(215,25%,15%)]">
                  {address.label}
                </p>
                <p className="text-xs text-muted-foreground">
                  {address.context}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && query.length >= 3 && !isLoading && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="px-4 py-3 text-sm text-muted-foreground text-center">
            Aucune adresse trouvée
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
